<template lang='pug'>
  .range-slider
    span(:class='`filter-span-${priceFilterClass(currentHandlePosition)}`')
    input.js-range-slider(
      ref='slider'
      :data-max='roundMinMax.max'
      :data-min='roundMinMax.min'
      :data-from='currentHandlePosition.from'
      :data-to='currentHandlePosition.to'
      :data-step='$t("currency.step", i18nSiteCountryLs)'
      :data-prefix='rangeSliderPrefix()'
      :data-postfix='rangeSliderPostfix()'
      :data-force-edges='true'
      :data-prettify-enabled='true'
    )
</template>

<script>
import { mapState } from 'vuex'
import { centsToDollars, dollarsToCents } from '@/modules/services/price-support'
import { pushDataLayerEvent } from '@/modules/services/gtm'

export default {
  data () {
    return {
      sliderElement: null
    }
  },

  computed: {
    ...mapState({
      availablePriceRange: state => state.product.availablePriceRange,
      filterPrice: state => state.product.filterPrice
    }),

    i18nSiteCountryLs: () => I18n.siteCountryLs,

    currentHandlePosition () {
      const filterMaxExceedBounds = this.filterPrice.max > this.availablePriceRange.max || this.filterPrice.max <= this.availablePriceRange.min
      const filterMinExceedBounds = this.filterPrice.min >= this.availablePriceRange.max || this.filterPrice.min < this.availablePriceRange.min

      if (
        (this.filterPrice.max && filterMaxExceedBounds) ||
        (this.filterPrice.min && filterMinExceedBounds)
      ) {
        return {
          to: this.roundMinMax.max,
          from: this.roundMinMax.min
        }
      }

      return {
        to: centsToDollars(this.filterPrice.max) || this.roundMinMax.max,
        from: centsToDollars(this.filterPrice.min) || this.roundMinMax.min
      }
    },

    roundMinMax () {
      const max = centsToDollars(this.availablePriceRange.max)
      const min = centsToDollars(this.availablePriceRange.min)
      const step = this.$t('currency.step', this.i18nSiteCountryLs)
      const roundMax = max % step !== 0
      const roundMin = min % step !== 0

      return {
        max: roundMax ? max + (step - max % step) : max,
        min: roundMin ? min - min % step : min
      }
    }
  },

  watch: {
    availablePriceRange () {
      this.sliderElement.update({
        min: this.roundMinMax.min,
        max: this.roundMinMax.max,
        from: this.roundMinMax.min,
        to: this.roundMinMax.max
      })
    },

    filterPrice () {
      this.sliderElement.update({
        from: centsToDollars(this.filterPrice.min || this.availableMinPrice),
        to: centsToDollars(this.filterPrice.max || this.availableMaxPrice)
      })
    }
  },

  mounted () {
    const slider = $(this.$refs.slider)
    slider.ionRangeSlider({
      type: 'double',
      prettify_separator: ',',
      onFinish: (event) => this.setFilterPrice(event.to, event.from)
    })
    this.sliderElement = slider.data('ionRangeSlider')
  },

  methods: {
    centsToDollars,

    priceFilterClass (range) {
      let className = 'price'
      if (range.min) {
        className += `-from-${range.min}`
      }
      if (range.max) {
        className += `-to-${range.max}`
      }
      return className
    },

    setFilterPrice (max, min) {
      const step = parseInt(this.$t('currency.step', this.i18nSiteCountryLs))

      // do not set url if slider handles are at the max or min
      let filterMax = (max !== this.roundMinMax.max) ? max : undefined
      let filterMin = (min !== this.roundMinMax.min) ? min : undefined

      // prevent handles from exchanging places
      if (dollarsToCents(min) !== this.filterPrice.min) {
        filterMin = (filterMin >= max) ? filterMin - step : filterMin
      }
      if (dollarsToCents(max) !== this.filterPrice.max) {
        filterMax = (filterMax <= min) ? filterMax + step : filterMax
      }

      const priceRange = {
        max: dollarsToCents(filterMax),
        min: dollarsToCents(filterMin)
      }

      pushDataLayerEvent('filterPriceChange', {
        min: filterMin,
        max: filterMax
      })
      this.$store.commit('toggleFilterPrice', priceRange)
    },

    rangeSliderPrefix () {
      if (this.i18nSiteCountryLs !== 'kr') {
        return this.$t('currency.symbol', this.i18nSiteCountryLs)
      }
    },

    rangeSliderPostfix () {
      if (this.i18nSiteCountryLs === 'kr') {
        return this.$t('currency.symbol', this.i18nSiteCountryLs)
      }
    }
  }
}
</script>

<style lang='scss'>
  @import '~sephora-style-guide/app/assets/stylesheets/sephora_style_guide/partials/slider';
</style>

<style lang='scss' scoped>
  .range-slider {
    width: 100%;
  }
</style>
